/**
 * Social authentication helpers (Apple / Google) via Supabase OAuth.
 *
 * Web: uses redirect-based OAuth.
 * Native iOS: uses expo-apple-authentication for Apple, expo-auth-session for Google.
 * Native Android: uses expo-auth-session for Google (Apple not available).
 */

import { supabase } from './supabase';
import { Platform } from 'react-native';

export const isSocialAuthAvailable = true;

// ── Apple Sign-In ──────────────────────────────────────────────

export async function signInWithApple() {
  if (Platform.OS === 'web') {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
    return data;
  }

  if (Platform.OS === 'ios') {
    const AppleAuthentication = require('expo-apple-authentication');
    const Crypto = require('expo-crypto');

    const nonce = Crypto.randomUUID();
    const hashedNonce = await Crypto.digestStringAsync(
      Crypto.CryptoDigestAlgorithm.SHA256,
      nonce,
    );

    const credential = await AppleAuthentication.signInAsync({
      requestedScopes: [
        AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
        AppleAuthentication.AppleAuthenticationScope.EMAIL,
      ],
      nonce: hashedNonce,
    });

    const identityToken = credential.identityToken;
    if (!identityToken) throw new Error('No identity token returned from Apple.');

    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: 'apple',
      token: identityToken,
      nonce,
    });
    if (error) throw error;
    return data;
  }

  // Android — Apple Sign-In not natively available
  throw new Error('Apple Sign-In is only available on iOS and web.');
}

// ── Google Sign-In ─────────────────────────────────────────────

export async function signInWithGoogle() {
  if (Platform.OS === 'web') {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) throw error;
    return data;
  }

  // Native (iOS / Android) — use expo-auth-session
  const { makeRedirectUri } = require('expo-auth-session');
  const { openAuthSessionAsync } = require('expo-web-browser');

  const redirectUri = makeRedirectUri();

  const { data: oauthData, error: oauthError } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: redirectUri,
      skipBrowserRedirect: true,
    },
  });

  if (oauthError) throw oauthError;
  if (!oauthData?.url) throw new Error('No OAuth URL returned from Supabase.');

  const result = await openAuthSessionAsync(oauthData.url, redirectUri);

  if (result.type !== 'success' || !result.url) {
    throw new Error('Google sign-in was cancelled or failed.');
  }

  // Extract tokens from the redirect URL fragment
  let accessToken: string | null = null;
  let refreshToken: string | null = null;
  try {
    const url = new URL(result.url);
    if (url.hash) {
      const fragment = new URLSearchParams(url.hash.substring(1));
      accessToken = fragment.get('access_token');
      refreshToken = fragment.get('refresh_token');
    }
  } catch {
    throw new Error('Invalid redirect URL from sign-in.');
  }

  if (!accessToken) throw new Error('No access token in redirect.');

  const { data, error } = await supabase.auth.setSession({
    access_token: accessToken,
    refresh_token: refreshToken ?? '',
  });

  if (error) throw error;
  return data;
}
